import 'regenerator-runtime/runtime';
import {
    vec2,
    mat2d
} from 'gl-matrix';
import * as twgl from 'twgl.js';
import * as dat from 'dat.gui';

import classes from './main.css';

const pointRadius = 5;
const pointHitRadius = 7;
let strokeThickness = 60;
let strokeWidth = strokeThickness / 2;
const pointRadiusSquared = pointHitRadius * pointHitRadius;
const points = [vec2.fromValues(100, 100), vec2.fromValues(100, 500), vec2.fromValues(500, 500), vec2.fromValues(600, 500)];
const styleMiter = 0;
const styleBevel = 1;
const styleRound = 2;
let style = styleRound;
let transparent = true;
let drawWire = true;
let drawLabels = true;
let drawPoints = true;
let closed = false;
const styles = {
    miter: styleMiter,
    bevel: styleBevel,
    round: styleRound
};


const capButt = 0;
const capRound = 1;
const capSquare = 2;
let capStyle = capRound;
const caps = {
    butt: capButt,
    round: capRound,
    square: capSquare
};

const joinStyles = {};
const styleToKey = new Map();
for (let key in styles) {
    joinStyles[key] = key;
    styleToKey.set(styles[key], key);
}
const capStyles = {};
const capStyleToKey = new Map();
for (let key in caps) {
    capStyles[key] = key;
    capStyleToKey.set(caps[key], key);
}

const gui = new dat.GUI({
    width: 300
});
gui.add({
    get join() {
        return styleToKey.get(style);
    },
    set join(value) {
        style = styles[value];
        buildStroke();
        draw();
    }
}, 'join', joinStyles);
gui.add({
    get cap() {
        return capStyleToKey.get(capStyle);
    },
    set cap(value) {
        capStyle = caps[value];
        buildStroke();
        draw();
    }
}, 'cap', capStyles);

gui.add({
    get drawWire() {
        return drawWire;
    },
    set drawWire(value) {
        drawWire = value;
        draw();
    }
}, 'drawWire', drawWire);

gui.add({
    get transparent() {
        return transparent;
    },
    set transparent(value) {
        transparent = value;
        draw();
    }
}, 'transparent', transparent);
gui.add({
    get drawLabels() {
        return drawLabels;
    },
    set drawLabels(value) {
        drawLabels = value;
        draw();
    }
}, 'drawLabels', drawLabels);
gui.add({
    get drawPoints() {
        return drawPoints;
    },
    set drawPoints(value) {
        drawPoints = value;
        draw();
    }
}, 'drawPoints', drawPoints);
gui.add({
    get strokeThickness() {
        return strokeThickness;
    },
    set strokeThickness(value) {
        strokeThickness = value;
        strokeWidth = strokeThickness / 2;
        buildStroke();
        draw();
    }
}, 'strokeThickness', 1, 150);
gui.add({
    get closed() {
        return closed;
    },
    set closed(value) {
        closed = value;
        buildStroke();
        draw();
    }
}, 'closed', closed);


const canvas = document.getElementById('2dCanvas');
const ctx = canvas.getContext('2d');

let hoverPoint = null;
let isDragging = false;

const vertices = [];
let basicPoints = [];

canvas.width = window.innerWidth;
canvas.height = window.innerHeight;

function draw() {

    ctx.beginPath();
    ctx.fillStyle = 'rgb(0,0,0, 0.0)'
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.clearRect(0, 0, canvas.width, canvas.height);


    if (vertices.length > 0 && drawWire) {
        let count = vertices.length / 2;
        if (count > 0) {

            ctx.lineWidth = 0.5;
            ctx.beginPath();
            ctx.moveTo(vertices[0], vertices[1]);
            for (let i = 1; i < count; i++) {
                ctx.lineTo(vertices[i * 2], vertices[i * 2 + 1]);
            }
            ctx.stroke();
        }

    }

    ctx.font = '22px sans-serif';
    ctx.lineWidth = 1;
    if (drawLabels) {
        for (const point of basicPoints) {
            ctx.fillStyle = 'rgb(255, 255, 255)'
            ctx.beginPath();
            ctx.arc(point.pos[0], point.pos[1], 3, 0, 2 * Math.PI);
            ctx.stroke();
            ctx.fill();
            ctx.fillStyle = 'rgb(0, 0, 0)'

            ctx.fillText(point.name, point.pos[0], point.pos[1] + 30);
        }
    }
    ctx.fillStyle = 'rgb(255, 255, 255)'
    ctx.fillText('Triangle Count: ' + (vertices.length / 2 - 2), 50, 50);


    ctx.lineWidth = 0.5;
    ctx.beginPath();
    if (drawPoints) {
        const point = points[0];
        ctx.moveTo(point[0], point[1]);
        for (let i = 1; i < points.length; i++) {
            const point = points[i];
            ctx.lineTo(point[0], point[1]);
        }
        // ctx.stroke();

        for (const point of points) {
            if (point == hoverPoint) {
                ctx.lineWidth = 2;
            } else {
                ctx.lineWidth = 1;
            }

            ctx.beginPath();
            ctx.arc(point[0], point[1], pointRadius, 0, 2 * Math.PI);

            ctx.fill();
            ctx.stroke();
        }
    }

    drawGL();
}

function hover(point) {
    if (hoverPoint == point) {
        return;
    }
    hoverPoint = point;
    draw();
}

function mouseMove(ev) {

    const cursor = vec2.fromValues(ev.clientX, ev.clientY);
    if (isDragging) {
        if (hoverPoint) {
            vec2.copy(hoverPoint, cursor);
            buildStroke();
            draw();
        }
        return;
    }

    let closest = Number.MAX_VALUE;
    let closestPoint = null;
    // find closest point
    for (const point of points) {
        const distance = vec2.squaredDistance(cursor, point);
        if (distance < closest && distance < pointRadiusSquared) {
            closestPoint = point;
            closest = distance;
        }
    }

    hover(closestPoint);
}

function mouseDown(ev) {
    isDragging = true;
}

function mouseUp(ev) {
    isDragging = false;
}

window.addEventListener('resize', function () {
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    draw();
});

window.addEventListener('mousemove', mouseMove);
window.addEventListener('mousedown', mouseDown);
window.addEventListener('mouseup', mouseUp);

function buildStroke() {
    buildBasicOutline();
}

function annotate(name, pos) {
    basicPoints.push({
        name,
        pos
    });
}

function buildBasicOutline() {
    basicPoints.length = 0;
    vertices.length = 0;

    let lastPoint = points[0];
    let lastDiff = vec2.subtract(vec2.create(), points[1], lastPoint);
    let lastLength = vec2.length(lastDiff);
    let lastDiffNormalized = vec2.scale(vec2.create(), lastDiff, 1 / lastLength);
    let lastA = vec2.add(vec2.create(), lastPoint, [lastDiffNormalized[1] * -strokeWidth, lastDiffNormalized[0] * strokeWidth]);
    let lastB = vec2.add(vec2.create(), lastPoint, [-lastDiffNormalized[1] * -strokeWidth, -lastDiffNormalized[0] * strokeWidth]);

    if (!closed) {
        switch (capStyle) {
            case capSquare: {
                let squareA = vec2.add(vec2.create(), lastA, [lastDiffNormalized[0] * -strokeWidth, lastDiffNormalized[1] * -strokeWidth]);
                let squareB = vec2.add(vec2.create(), lastB, [lastDiffNormalized[0] * -strokeWidth, lastDiffNormalized[1] * -strokeWidth]);
                vertices.push(squareA[0], squareA[1]);
                vertices.push(squareB[0], squareB[1]);
                break;
            }
            case capRound: {
                let capDirection = [-lastDiff[1], lastDiff[0]];
                const range = Math.PI;
                const arcLength = Math.abs(range * strokeWidth);
                let steps = Math.ceil(arcLength / 4);
                let angleTo = Math.atan2(capDirection[1], capDirection[0]);
                let inc = range / steps;
                let angle = angleTo;
                // make sure to draw the full cap due triangle strip
                for (let j = 0; j <= steps; j++) {
                    vertices.push(lastPoint[0], lastPoint[1]);
                    vertices.push(lastPoint[0] + Math.cos(angle) * strokeWidth,
                        lastPoint[1] + Math.sin(angle) * strokeWidth);

                    angle += inc;
                }
                break;
            }
        }
    }

    vertices.push(lastA[0], lastA[1]);
    vertices.push(lastB[0], lastB[1]);
    // const lastBisector = [-lastDiffNormalized[1], lastDiffNormalized[0]];
    annotate('A', lastA);
    annotate('B', lastB);


    let pointLength = points.length;
    if (closed) {
        pointLength++;
    }
    for (let i = 1; i < pointLength; i++) {
        let point = points[i % points.length];
        let diff, diffNormalized, length, next;
        if (i < pointLength - 1 || closed) {
            diff = vec2.subtract(vec2.create(), (next = points[(i + 1) % points.length]), point);
            length = vec2.length(diff);
            diffNormalized = vec2.scale(vec2.create(), diff, 1 / length);
        } else {
            diff = lastDiff;
            next = point;
            length = lastLength;
            diffNormalized = lastDiffNormalized;
        }


        // perpendicular dx
        const pdx0 = -lastDiffNormalized[1];
        const pdy0 = lastDiffNormalized[0];
        const pdx1 = -diffNormalized[1];
        const pdy1 = diffNormalized[0];

        // Compute bisector without a normalization by averaging perpendicular diffs.
        const dmx = (pdx0 + pdx1) * 0.5;
        const dmy = (pdy0 + pdy1) * 0.5;

        const bisector = vec2.fromValues(dmx, dmy);
        const cross = diff[0] * lastDiff[1] - lastDiff[0] * diff[1];

        const dot = vec2.dot(bisector, bisector);

        const lengthLimit = Math.min(length, lastLength);
        let bevelInner = false;
        let bevel = style == styleMiter ? dot < 0.1 : dot < 0.999;
        if (dot > 0.000001) {
            let scale = 1.0 / dot * strokeWidth;
            const limit = lengthLimit / strokeWidth;
            if (dot * limit * limit < 1) {
                bevelInner = true;
            }
            bisector[0] *= scale;
            bisector[1] *= scale;
        } else {
            bisector[0] *= strokeWidth;
            bisector[1] *= strokeWidth;
        }



        if (!bevel) {
            let c = vec2.add(vec2.create(), point, [bisector[0], bisector[1]]);
            annotate('C', c);

            let d = vec2.add(vec2.create(), point, [-bisector[0], -bisector[1]]);
            annotate('D', d);

            if (!bevelInner) {
                vertices.push(c[0], c[1]);
                vertices.push(d[0], d[1]);
            } else if (cross <= 0) {
                const c1 = vec2.add(vec2.create(), point, [lastDiffNormalized[1] * -strokeWidth, lastDiffNormalized[0] * strokeWidth]);
                const c2 = vec2.add(vec2.create(), point, [diffNormalized[1] * -strokeWidth, diffNormalized[0] * strokeWidth]);

                annotate('C1', c1);
                annotate('C2', c2);

                vertices.push(c1[0], c1[1]);
                vertices.push(d[0], d[1]);
                vertices.push(c2[0], c2[1]);
                vertices.push(d[0], d[1]);
            } else {
                const d1 = vec2.add(vec2.create(), point, [-lastDiffNormalized[1] * -strokeWidth, -lastDiffNormalized[0] * strokeWidth]);
                const d2 = vec2.add(vec2.create(), point, [-diffNormalized[1] * -strokeWidth, -diffNormalized[0] * strokeWidth]);

                annotate('D1', d1);
                annotate('D2', d2);
                vertices.push(c[0], c[1]);
                vertices.push(d1[0], d1[1]);
                vertices.push(c[0], c[1]);
                vertices.push(d2[0], d2[1]);
            }
        } else if (cross <= 0) {

            const a1 = vec2.create();
            let a2 = a1;
            if (bevelInner) {
                a2 = vec2.create();
                vec2.add(a1, point, [lastDiffNormalized[1] * -strokeWidth, lastDiffNormalized[0] * strokeWidth]);
                vec2.add(a2, point, [diffNormalized[1] * -strokeWidth, diffNormalized[0] * strokeWidth]);
                annotate('A1', a1);
                annotate('A2', a2);
            } else {
                vec2.add(a1, point, [bisector[0], bisector[1]]);
                annotate('C', a1);
            }

            let b = vec2.add(vec2.create(), point, [-lastDiffNormalized[1] * -strokeWidth, -lastDiffNormalized[0] * strokeWidth]);
            let bn = vec2.add(vec2.create(), point, [-diffNormalized[1] * -strokeWidth, -diffNormalized[0] * strokeWidth]);
            annotate('B', b);
            annotate('Bn', bn);

            vertices.push(a1[0], a1[1]);
            vertices.push(b[0], b[1]);
            if (style == styleRound) {
                const pivot = bevelInner ? point : a1;
                let toPrev = vec2.subtract(vec2.create(), bn, point);
                let toNext = vec2.subtract(vec2.create(), b, point);
                let angleFrom = Math.atan2(toPrev[1], toPrev[0]);
                let angleTo = Math.atan2(toNext[1], toNext[0]);
                if (angleTo > angleFrom) angleTo -= Math.PI * 2;
                const range = angleTo - angleFrom;
                const arcLength = Math.abs(range * strokeWidth);
                let steps = Math.ceil(arcLength / 4);

                let inc = range / steps;
                let angle = angleTo - inc;
                for (let j = 0; j < steps - 1; j++) {
                    vertices.push(pivot[0], pivot[1]);
                    vertices.push(point[0] + Math.cos(angle) * strokeWidth,
                        point[1] + Math.sin(angle) * strokeWidth);

                    angle -= inc;
                }
            }
            vertices.push(a2[0], a2[1]);
            vertices.push(bn[0], bn[1]);
        } else {
            const b1 = vec2.create();
            let b2 = b1;
            if (bevelInner) {
                b2 = vec2.create();
                vec2.add(b1, point, [-lastDiffNormalized[1] * -strokeWidth, -lastDiffNormalized[0] * strokeWidth]);
                vec2.add(b2, point, [-diffNormalized[1] * -strokeWidth, -diffNormalized[0] * strokeWidth]);
                annotate('B2', b2);
                annotate('B1', b1);
            } else {
                vec2.add(b1, point, [-bisector[0], -bisector[1]]);
                annotate('D', b1);
            }


            let a = vec2.add(vec2.create(), point, [lastDiffNormalized[1] * -strokeWidth, lastDiffNormalized[0] * strokeWidth]);
            let an = vec2.add(vec2.create(), point, [diffNormalized[1] * -strokeWidth, diffNormalized[0] * strokeWidth]);
            annotate('A', a);
            annotate('An', an);


            vertices.push(a[0], a[1]);
            vertices.push(b1[0], b1[1]);
            if (style == styleRound) {
                const pivot = bevelInner ? point : b1;
                let toPrev = vec2.subtract(vec2.create(), a, point);
                let toNext = vec2.subtract(vec2.create(), an, point);
                let angleFrom = Math.atan2(toPrev[1], toPrev[0]);
                let angleTo = Math.atan2(toNext[1], toNext[0]);
                if (angleTo > angleFrom) angleTo -= Math.PI * 2;

                const range = angleTo - angleFrom;
                const arcLength = Math.abs(range * strokeWidth);
                let steps = Math.ceil(arcLength / 4);
                let inc = range / steps;

                let angle = angleFrom + inc;
                for (let j = 0; j < steps - 1; j++) {
                    vertices.push(point[0] + Math.cos(angle) * strokeWidth,
                        point[1] + Math.sin(angle) * strokeWidth);
                    vertices.push(pivot[0], pivot[1]);
                    angle += inc;
                }
            }
            vertices.push(an[0], an[1]);
            vertices.push(b2[0], b2[1]);
        }

        lastPoint = point;
        lastDiff = diff;
        lastDiffNormalized = diffNormalized;
    }
    if (closed) {
        const last = vertices.length - 1;
        vertices[0] = vertices[last - 3];
        vertices[1] = vertices[last - 2];
        vertices[2] = vertices[last - 1];
        vertices[3] = vertices[last];
        // last 4 vertices can be ignored.
    } else {
        switch (capStyle) {
            case capSquare: {
                const l = vertices.length;

                let squareA = vec2.add(vec2.create(), [vertices[l - 4], vertices[l - 3]], [lastDiffNormalized[0] * strokeWidth, lastDiffNormalized[1] * strokeWidth]);
                let squareB = vec2.add(vec2.create(), [vertices[l - 2], vertices[l - 1]], [lastDiffNormalized[0] * strokeWidth, lastDiffNormalized[1] * strokeWidth]);
                vertices.push(squareA[0], squareA[1]);
                vertices.push(squareB[0], squareB[1]);
                break;
            }
            case capRound: {
                let capDirection = [-lastDiff[1], lastDiff[0]];
                const range = Math.PI;
                const arcLength = Math.abs(range * strokeWidth);
                let steps = Math.ceil(arcLength / 4);
                let angleTo = Math.atan2(capDirection[1], capDirection[0]);
                let inc = range / steps;
                let angle = angleTo;
                // make sure to draw the full cap due triangle strip
                for (let j = 0; j <= steps; j++) {
                    vertices.push(lastPoint[0], lastPoint[1]);
                    vertices.push(lastPoint[0] + Math.cos(angle) * strokeWidth,
                        lastPoint[1] + Math.sin(angle) * strokeWidth);

                    angle -= inc;
                }
                break;
            }
        }
    }
}
// ---- GL RENDERER ----
const glCanvas = document.getElementById('glCanvas');
const gl = glCanvas.getContext('webgl', {
    powerPreference: 'high-performance',
    preserveDrawingBuffer: true,
    alpha: true,
    stencil: true,
    antialias: true,
    premultipliedAlpha: false,
});

const programInfo = twgl.createProgramInfo(gl, [
    `
attribute vec2 position;
uniform mat4 projection;

void main(void) {
    gl_Position = projection*vec4(position, 0.0, 1.0);
}`,
    `
precision highp float;
uniform float alpha;
void main() {
    gl_FragColor = vec4(1.0, 1.0, 1.0, alpha);
}`
]);

let vertexBuffer;

function drawGL() {
    glCanvas.width = window.innerWidth;
    glCanvas.height = window.innerHeight;
    gl.viewport(0, 0, glCanvas.width, glCanvas.height);
    gl.clearColor(0.3, 0.3, 0.3, 1.0);
    gl.clear(gl.COLOR_BUFFER_BIT | gl.STENCIL_BUFFER_BIT);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

    const projection = twgl.m4.ortho(
        0,
        glCanvas.width,
        glCanvas.height,
        0,
        0,
        1
    );

    if (vertexBuffer) {
        gl.deleteBuffer(vertexBuffer.attribs.position.buffer);
    }
    // vertices.length = 0;
    // vertices.push(-1, 1, 1, 1, -1, -1, 1, -1);
    // vertices.push(0, 500, 100, 500, 0, 500 + 100, 100, 500 + 100);
    vertexBuffer = twgl.createBufferInfoFromArrays(gl, {
        position: {
            numComponents: 2,
            data: new Float32Array(vertices)
        },
    });


    const uniforms = {
        projection: projection,
        alpha: transparent ? 0.5 : 1.0
    };

    gl.useProgram(programInfo.program);
    gl.disable(gl.DEPTH_TEST);
    gl.disable(gl.CULL_FACE);
    twgl.setBuffersAndAttributes(gl, programInfo, vertexBuffer);
    twgl.setUniforms(programInfo, uniforms);
    twgl.drawBufferInfo(gl, vertexBuffer, gl.TRIANGLE_STRIP, vertices.length / 2);
}


buildStroke();
draw();